<app-page-header title="User Definitions" subtitle="Manage system users, roles and access permissions">
    <div actions class="flex gap-2">
        <p-button [outlined]="true" label="Filters" (click)="openFilter()" icon="pi pi-filter"></p-button>
        <p-iconfield>
            <p-inputicon class="pi pi-search" />
            <input type="text" pInputText placeholder="Search" (input)="onGlobalSearch($event)" />
        </p-iconfield>
        <p-button label="Create New" (click)="openCreateUser()" icon="pi pi-plus"></p-button>
    </div>
</app-page-header>
<div class="table-card">
    <p-table
        #dt
        [value]="users"
        [paginator]="true"
        [loading]="loading"
        [rows]="10"
        [rowsPerPageOptions]="[10, 25, 50]"
        [scrollable]="true"
        scrollHeight="flex"
        dataKey="id"
        [rowHover]="true"
        [globalFilterFields]="['userName', 'name', 'email', 'agent.name', 'status', 'createdBy', 'modifiedBy', 'role']"
    >
        <ng-template #header let-columns>
            <tr>
                <th *ngFor="let col of cols" [pSortableColumn]="col.field">
                    <div>
                        {{ col.header }}
                        <p-sortIcon [field]="col.field"></p-sortIcon>
                        <app-column-filter [table]="dt" type="text" [field]="col.field" display="menu"></app-column-filter>
                    </div>
                </th>
                <th></th>
            </tr>
        </ng-template>
        <ng-template #body let-user>
            <tr>
                <td>{{ user.userName }}</td>
                <td>{{ user.name }}</td>
                <td>{{ user.email }}</td>
                <td>
                    <a class="text-blue-600 underline" target="_blank" [href]="user.agentLink">{{ user.agent.name }}</a>
                </td>
                <td>
                    <span [ngClass]="getStatusClass(user.status)" class="!p-2">{{ friendlyName(user.status) }}</span>
                </td>
                <td>
                    {{ user.createdBy }}
                </td>
                <td>
                    {{ user.createdDate | date: 'dd.MM.yyyy HH:mm' }}
                </td>
                <td>
                    {{ user.modifiedBy }}
                </td>
                <td>
                    {{ user.modifiedDate | date: 'dd.MM.yyyy HH:mm' }}
                </td>
                <td>
                    {{ user.passwordExpiry | date: 'dd.MM.yyyy HH:mm' }}
                </td>

                <td>
                    <p-button
                        *ngIf="user.editable"
                        severity="secondary"
                        icon="pi pi-pencil"
                        (click)="openEditUser(user)"
                        variant="text"
                        class="p-button-text"
                    >
                    </p-button>
                </td>
            </tr>
        </ng-template>
    </p-table>
</div>

<p-drawer header="Filters" [(visible)]="filterVisible" position="right">
    <div class="h-full flex flex-col">
        <div class="flex-1 overflow-auto">
            <div class="flex flex-col items-start gap-2 mb-6">
                <label for="userDisplayToggle" class="font-semibold text-lg"> User's Display </label>
                <div class="flex items-center mt-2">
                    <p-toggleswitch [(ngModel)]="userStatusChecked" inputId="userDisplayToggle" />
                    <label for="userDisplayToggle" class="ml-2">Show WS Users</label>
                </div>
            </div>

            <div class="flex flex-col items-start gap-2">
                <label for="userStatusToggle" class="font-semibold text-lg"> User's Status </label>
                <div class="flex items-center mt-2">
                    <p-toggleswitch [(ngModel)]="userDisplayChecked" inputId="userStatusToggle" />
                    <label for="userStatusToggle" class="ml-2">Show Users That Status NOT_OK</label>
                </div>
            </div>
        </div>
        <div class="mt-auto border-t pt-4">
            <div class="flex justify-end gap-2 pb-4">
                <p-button label="Cancel" variant="outlined" (click)="cancelFilter()"> </p-button>
                <p-button label="Apply Filters" (click)="onToggleChanged()"> </p-button>
            </div>
        </div>
    </div>
</p-drawer>
<p-dialog
    [header]="isEditMode ? 'Edit User' : 'New User'"
    [(visible)]="userDialogVisible"
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    [style]="{ width: '800px', maxHeight: '100vh' }"
    [appendTo]="'body'"
    [contentStyle]="{ overflow: 'auto', maxHeight: 'calc(100vh - 120px)' }"
>
    <form [formGroup]="userForm" (ngSubmit)="saveUser()" #userFormRef="ngForm">
        <div class="grid grid-cols-2 gap-6">
            <div class="flex flex-col gap-4">
                <div class="flex flex-col">
                    <label class="text-sm font-semibold mb-2">User Name *</label>
                    <input
                        type="text"
                        pInputText
                        placeholder="Write a user name"
                        formControlName="userName"
                        [ngClass]="{ 'ng-invalid ng-dirty': isInvalid('userName') }"
                    />
                    @if (isInvalid('userName')) {
                        <small class="text-red-500" *ngIf="userForm.get('userName')?.errors?.['required']"> Username is required. </small>
                        <small class="text-red-500" *ngIf="userForm.get('userName')?.errors?.['pattern']">
                            Username cannot have numbers or special characters
                        </small>
                    }
                </div>
                <div class="flex flex-col">
                    <label class="text-sm font-semibold mb-2">Agent *</label>
                    <p-select
                        formControlName="agent"
                        panelStyleClass="!w-full"
                        [options]="agentList"
                        optionLabel="name"
                        optionValue="id"
                        appendTo="body"
                        placeholder="Select an agent"
                        [ngClass]="{ 'ng-invalid ng-dirty': isInvalid('agent') }"
                    >
                    </p-select>
                    @if (isInvalid('agent')) {
                        <small class="text-red-500"> Agent is required. </small>
                    }
                </div>
                <div class="flex flex-col">
                    <label class="text-sm font-semibold mb-2">Name *</label>
                    <input
                        type="text"
                        class="uppercase placeholder:normal-case"
                        pInputText
                        formControlName="name"
                        placeholder="Write a fullname"
                        [ngClass]="{ 'ng-invalid ng-dirty': isInvalid('name') }"
                    />
                    @if (isInvalid('name')) {
                        <small class="text-red-500" *ngIf="userForm.get('name')?.errors?.['required']"> Name is required. </small>
                        <small class="text-red-500" *ngIf="userForm.get('name')?.errors?.['pattern']">
                            Name cannot have numbers or special characters.
                        </small>
                    }
                </div>
                <div class="flex flex-col">
                    <label class="text-sm font-semibold mb-2">Job Title (Optional)</label>
                    <input type="text" pInputText formControlName="jobTitle" placeholder="Write a job title" />
                </div>
                <div class="flex flex-col">
                    <label class="text-sm font-semibold mb-2">Work Phone (Optional)</label>
                    <input
                        type="text"
                        [ngClass]="{ 'ng-invalid ng-dirty': isInvalid('workPhone') }"
                        pInputText
                        formControlName="workPhone"
                        placeholder="Write a work phone"
                    />
                    @if (isInvalid('workPhone')) {
                        <small class="text-red-500" *ngIf="userForm.get('workPhone')?.errors?.['pattern']">
                            Work Phone can only contain numbers and an optional leading + sign.
                        </small>
                    }
                </div>
                <div class="flex flex-col">
                    <label class="text-sm font-semibold mb-2">Roles (Optional)</label>
                    <p-multi-select
                        formControlName="role"
                        [options]="roleList"
                        panelStyleClass="!w-full"
                        appendTo="body"
                        display="chip"
                        showClear="true"
                        filter="false"
                        placeholder="Select a role"
                        optionLabel="explanation"
                        optionValue="id"
                    >
                    </p-multi-select>
                </div>
                @if (isEditMode && selectedUser.passwordChangeable) {
                    <div class="flex flex-col">
                        <p-button
                            iconPos="left"
                            [icon]="showChangePasswordFields ? 'pi pi-eye' : 'pi pi pi-eye-slash'"
                            (click)="toggleShowChangePasswordFields()"
                            variant="outlined"
                            [label]="showChangePasswordFields ? 'Hide Change Password' : 'Show Change Password'"
                        />
                    </div>
                    @if (showChangePasswordFields) {
                        <div class="flex flex-col">
                            <label class="text-sm font-semibold mb-2">Current Password *</label>
                            <input
                                type="password"
                                pInputText
                                formControlName="currentPassword"
                                placeholder="Enter current password"
                                [ngClass]="{ 'ng-invalid ng-dirty': isInvalid('currentPassword') }"
                            />

                            @if (isInvalid('currentPassword')) {
                                <small class="text-red-500" *ngIf="userForm.get('currentPassword')?.errors?.['required']">
                                    Current Password is required.
                                </small>
                            }
                        </div>

                        <div class="flex flex-col">
                            <label class="text-sm font-semibold mb-2">New Password *</label>
                            <input
                                type="password"
                                pInputText
                                formControlName="newPassword"
                                placeholder="Enter new password"
                                [ngClass]="{ 'ng-invalid ng-dirty': isInvalid('newPassword') }"
                            />
                            @if (isInvalid('newPassword')) {
                                <small class="text-red-500" *ngIf="userForm.get('newPassword')?.errors?.['required']">
                                    New Password is required.
                                </small>
                                <small class="text-red-500" *ngIf="userForm.get('newPassword')?.errors?.['minlength']">
                                    New Password must be at least 8 characters long.
                                </small>
                                <small class="text-red-500" *ngIf="userForm.get('newPassword')?.errors?.['passwordStrength']">
                                    New Password must contain at least one uppercase letter, one lowercase letter, one number, and one
                                    special character.
                                </small>
                            }
                        </div>

                        <div class="flex flex-col">
                            <label class="text-sm font-semibold mb-2">Retry New Password *</label>
                            <input
                                type="password"
                                pInputText
                                formControlName="retryNewPassword"
                                placeholder="Retry new password"
                                [ngClass]="{ 'ng-invalid ng-dirty': isInvalid('retryNewPassword') }"
                            />
                            @if (isInvalid('retryNewPassword')) {
                                <small class="text-red-500" *ngIf="userForm.get('retryNewPassword')?.errors?.['required']">
                                    Retry New Password is required.
                                </small>
                                <small class="text-red-500" *ngIf="userForm.get('retryNewPassword')?.errors?.['passwordMismatch']">
                                    Retry New Password does not match New Password.
                                </small>
                            }
                        </div>
                    }
                }
            </div>
            <div class="flex flex-col gap-4">
                @if (!isEditMode) {
                    <div class="flex flex-col">
                        <label class="text-sm font-semibold mb-2">Password *</label>
                        <input
                            type="password"
                            pInputText
                            formControlName="password"
                            placeholder="Write a password"
                            [ngClass]="{ 'ng-invalid ng-dirty': isInvalid('password') }"
                        />
                        @if (isInvalid('password')) {
                            <small class="text-red-500" *ngIf="userForm.get('password')?.errors?.['required']">
                                Password is required.
                            </small>
                            <small class="text-red-500" *ngIf="userForm.get('password')?.errors?.['minlength']">
                                Password must be at least 8 characters long.
                            </small>
                            <small class="text-red-500" *ngIf="userForm.get('password')?.errors?.['passwordStrength']">
                                Password must contain at least one uppercase letter, one lowercase letter, one number, and one special
                                character.
                            </small>
                        }
                    </div>
                }
                <div class="flex flex-col">
                    <label class="text-sm font-semibold mb-2">Email *</label>
                    <input
                        type="email"
                        [email]="true"
                        pInputText
                        formControlName="email"
                        placeholder="Write an email"
                        [ngClass]="{ 'ng-invalid ng-dirty': isInvalid('email') }"
                    />

                    @if (isInvalid('email')) {
                        <small class="text-red-500" *ngIf="userForm.get('email')?.errors?.['required']"> Email is required. </small>
                        <small class="text-red-500" *ngIf="userForm.get('email')?.errors?.['email']">
                            Please enter a valid email address.
                        </small>
                    }
                </div>
                <div class="flex flex-col">
                    <label class="text-sm font-semibold mb-2">Department (Optional)</label>
                    <input type="text" pInputText formControlName="department" placeholder="Write a department" />
                </div>
                <div class="flex flex-col">
                    <label class="text-sm font-semibold mb-2">Mobile Phone (Optional)</label>
                    <input
                        type="text"
                        [ngClass]="{ 'ng-invalid ng-dirty': isInvalid('mobilePhone') }"
                        pInputText
                        formControlName="mobilePhone"
                        placeholder="Write a mobile phone"
                    />
                    @if (isInvalid('mobilePhone')) {
                        <small class="text-red-500" *ngIf="userForm.get('mobilePhone')?.errors?.['pattern']">
                            Mobile Phone can only contain numbers and an optional leading + sign.
                        </small>
                    }
                </div>
                <div class="flex flex-col">
                    <label class="text-sm font-semibold mb-2">Language (Optional)</label>
                    <input type="text" pInputText formControlName="language" placeholder="Write a language" />
                </div>

                <div class="flex flex-col">
                    <label class="text-sm font-semibold mb-2">Status (Optional)</label>
                    <p-select
                        formControlName="status"
                        [options]="statusList"
                        panelStyleClass="!w-full"
                        placeholder="Select a status"
                        appendTo="body"
                    >
                    </p-select>
                </div>
            </div>
        </div>
    </form>
    <ng-template pTemplate="footer">
        <div class="flex justify-between w-full">
            <button type="button" class="text-blue-600 hover:text-blue-800 underline" (click)="clearChanges()">Clear changes</button>
            <div class="flex gap-2">
                <p-button label="Cancel" variant="outlined" (click)="cancelUserFilter()"> </p-button>
                <p-button [label]="isEditMode ? 'Update' : 'Create'" type="submit" (click)="saveUser()"> </p-button>
            </div>
        </div>
    </ng-template>
</p-dialog>
